// src\themes\lino\components\VideoBanner.tsx
'use client';

import Link from 'next/link';
import Image from 'next/image';
import { Play } from 'lucide-react';
import { motion } from 'motion/react';
import { textGroupVariants, textItemVariants } from './animations/textReveal';

const isVideoUrl = (url?: string) => {
    if (!url) return false;
    return /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url);
};

interface VideoBannerProps {
    data?: {
        title?: string | null;
        buttonContent?: string | null;
        link?: string | null;
        image_info?: { url: string; description?: string } | null;
    };
}

export default function VideoBanner({ data }: VideoBannerProps) {
    if (!data || !data.image_info?.url) return null;

    const isVideo = isVideoUrl(data.image_info.url);

    const content = (
        <div className="relative w-full h-[65vh] overflow-hidden rounded-2xl">
            {isVideo ? (
                <video
                    src={data.image_info.url}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="absolute inset-0 w-full h-full object-cover"
                />
            ) : (
                <Image
                    src={data.image_info.url}
                    alt={data.image_info.description || data.title || ''}
                    fill
                    className="object-cover"
                />
            )}

            <div className="absolute inset-0 bg-primary-950/45" />

            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.5 }}
                variants={textGroupVariants}
                className="absolute inset-0 flex flex-col items-center justify-center gap-5 text-primary-0 text-center"
            >
                {data.title && (
                    <motion.h2 variants={textItemVariants} className="text-3xl md:text-6xl tracking-[-0.02em] font-light">
                        {data.title}
                    </motion.h2>
                )}
                {data.buttonContent && (
                    <motion.span variants={textItemVariants} className="flex items-center gap-4 rounded-full border border-primary-0/70 px-2 py-2 pl-6 backdrop-blur-sm text-[11px] uppercase tracking-[0.14em]">
                        {data.buttonContent}
                        <span className="flex items-center justify-center rounded-full bg-primary-0 size-9">
                            <Play className="size-4 text-primary-900 fill-primary-900" />
                        </span>
                    </motion.span>
                )}
            </motion.div>
        </div>
    );

    return data.link ? <Link href={data.link}>{content}</Link> : content;
}